import {
  FmlxButton,
  FmlxCheckBox,
  FmlxDropdown,
  FmlxIcon,
  FmlxPagination,
  FmlxProgressBar,
  FmlxTableBody,
  FmlxTableCell,
  FmlxTableContainer,
  FmlxTableFooter,
  FmlxTableHead,
  FmlxTableHeadCell,
  FmlxTableRow,
  FmlxTextInput,
} from 'fmlx-common-ui';
import React, {useMemo} from 'react';
import './EssSimpleTable.scss';

const EssSimpleTable = ({
  columnFilterSelectedIndex,
  columns,
  onSort,
  onCollapse,
  openRows,
  classNameBy,
  columnFilterRefs,
  columnFilterHandlers,
  columnFilterDisabled,
  filteredData,
  collapsibleContentTable,
  columnData,
  dataCount,
  rowsPerPage,
  page,
  onChangePage,
  onChangeRowsPerPage,
  onPageOption,
  targetValue,
  hideHeader = false,
  hideFooter = false,
  emptyRowHeight = 50,
  onEmptyDataShow = 'Entries not found',
  sortTableDirection,
  onSelectAll = () => {},
  selectedRowIds,
  sortKey,
  showDisplaySettings = false,
}) => {
  // let indexCounter = 0;
  const generateUniqueId = (page, index) => `row_${page}_${index}`;

  const processedData = filteredData.map((row, index) => {
    const id = row.id || generateUniqueId(page, index);
    return {...row, id};
  });

  const getUniqueColumnValues = (accessor, data) => {
    const uniqueValues = [...new Set(data.map((item) => item[accessor]))];
    return uniqueValues.map((value, index) => ({
      id: index,
      text: value,
      group: 'default',
      disabled: false,
    }));
  };

  const hasCollapsibleData = useMemo(() => {
    return processedData.some((item) => item.isCollapsible);
  }, [processedData]);

  return (
    <div className="EssSimpleTableContainer">
      <FmlxTableContainer>
        {!hideHeader && (
          <FmlxTableHead>
            <FmlxTableRow>
              {hasCollapsibleData && <FmlxTableHeadCell></FmlxTableHeadCell>}
              {columnData.map((column) => (
                <FmlxTableHeadCell
                  sortDisabled={!column.sortable}
                  onClickSort={() => column.sortable && onSort(column.accessor)}
                  sortDirection={column.accessor === sortKey ? `${sortTableDirection}` : ''}
                  key={column.id}
                  label={column.Header}
                  className={
                    classNameBy === targetValue && [column.accessor]
                      ? `${column.accessor}-column`
                      : `${column.accessor}-default-column`
                  }
                >
                  {column.accessor === 'CHECKBOX' && (
                    <FmlxCheckBox
                      indeterminate={selectedRowIds.length > 0 && selectedRowIds.length < filteredData.length}
                      checked={filteredData.length > 0 && selectedRowIds.length === filteredData.length}
                      onChange={onSelectAll}
                    />
                  )}
                  {columnFilterRefs && columnFilterRefs[column.accessor] && (
                    <div
                      className={
                        classNameBy !== targetValue ? `${column.accessor}-default-column` : `${column.accessor}-column`
                      }
                    >
                      {column.filter === 'TEXT' && (
                        <FmlxTextInput
                          ref={columnFilterRefs[column.accessor]}
                          placeholder={`Search ${column.accessor}`}
                          onChange={(event) => columnFilterHandlers[column.accessor](event)}
                          size={column.accessor}
                          disabled={columnFilterDisabled?.[column.accessor]}
                        />
                      )}
                      {column.filter === 'DROPDOWN' && (
                        <FmlxDropdown
                          items={column.items || getUniqueColumnValues(column.accessor, processedData)}
                          placeholder={`Select ${column.accessor}`}
                          onChange={(event) => {
                            columnFilterHandlers.dropdown(event, column.accessor);
                          }}
                          selectedItem={columnFilterSelectedIndex?.[column.accessor] ?? -1}
                          disabled={columnFilterDisabled?.[column.accessor]}
                        />
                      )}
                    </div>
                  )}
                </FmlxTableHeadCell>
              ))}
            </FmlxTableRow>
          </FmlxTableHead>
        )}

        <FmlxTableBody>
          {processedData.length > 0 ? (
            processedData.map((row) => (
              <React.Fragment key={row.id}>
                {/* Main row */}
                <FmlxTableRow hover>
                  {/* Chevron button for collapsible content */}
                  {hasCollapsibleData && (
                    <FmlxTableCell key={row.id}>
                      {hasCollapsibleData && (
                        <FmlxButton
                          onlyIcon
                          size={FmlxButton.Size.SMALL}
                          variant={FmlxButton.Variant.PLAIN}
                          withTooltip={false}
                          icon={openRows[row.id] ? <FmlxIcon name="ChevronUp" /> : <FmlxIcon name="ChevronDown" />}
                          onClick={() => onCollapse(row.id)}
                          type={FmlxButton.Type.BASIC}
                        />
                      )}
                    </FmlxTableCell>
                  )}
                  {columnData.map((column) => (
                    <FmlxTableCell
                      key={column.accessor}
                      align={FmlxTableCell.Align.LEFT}
                      className={
                        classNameBy === targetValue && (column.accessor === 'sender' || column.accessor === 'receiver')
                          ? `${column.accessor}-column`
                          : ''
                      }
                    >
                      {/* {columns(column, row)} */}
                      {/* {column.renderCell ? column.renderCell(column, row) : row[column.accessor]} */}
                      {column.renderCell
                        ? column.renderCell(column, row)
                        : columns
                        ? columns(column, row)
                        : row[column.accessor]}
                    </FmlxTableCell>
                  ))}
                </FmlxTableRow>

                {/* Collapsible content row */}
                {hasCollapsibleData && openRows[row.id] && <>{collapsibleContentTable(row)}</>}
              </React.Fragment>
            ))
          ) : (
            <FmlxTableRow hover style={{height: emptyRowHeight}}>
              <FmlxTableCell colSpan={(columns || columnData).length} style={{textAlign: 'center'}}>
                <strong>
                  <FmlxIcon name={'FileOutlined'} /> {`${onEmptyDataShow}`}
                </strong>
              </FmlxTableCell>
            </FmlxTableRow>
          )}
        </FmlxTableBody>
        {!hideFooter && (
          <FmlxTableFooter>
            <FmlxTableRow>
              <FmlxTableCell colSpan={columnData.length + (hasCollapsibleData ? 1 : 0)}>
                <FmlxPagination
                  id="ess-pagination"
                  variant="tablePagination"
                  rowsPerPageOptions={onPageOption}
                  count={dataCount.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={onChangePage}
                  onRowsPerPageChange={onChangeRowsPerPage}
                  showItemsPerPage
                  showFirstLastButton
                  showDisplaySettings={showDisplaySettings}
                />
              </FmlxTableCell>
            </FmlxTableRow>
          </FmlxTableFooter>
        )}
      </FmlxTableContainer>
    </div>
  );
};

export default EssSimpleTable;
